import { z } from 'zod'
import { optionalText, versionSchema } from './common.js'

/** The legal identity printed on invoices, statements and reports. */
export const companyProfileSchema = z.object({
  legalName: optionalText(200),
  legalNameAr: optionalText(200),
  /** VAT registration number (Oman: OM followed by 10 digits). */
  vatNumber: optionalText(30),
  /** Commercial registration number. */
  crNumber: optionalText(30),
  address: optionalText(500),
  addressAr: optionalText(500),
  phone: optionalText(30),
  email: z
    .email()
    .nullish()
    .transform((v) => v ?? null),
  /**
   * The name shown in the app and as the fallback on prints (the tenant's name, settings Q9/G-C3).
   * Left out: unchanged (so a form without it never clears it). It cannot be blank.
   */
  displayName: z.string().trim().min(1).max(200).optional(),
  /** Left out: unchanged; null or '' clears it. */
  displayNameAr: z
    .string()
    .trim()
    .max(200)
    .nullish()
    .transform((v) => (v === undefined ? undefined : v || null)),
  version: versionSchema,
})
export type CompanyProfileUpdate = z.input<typeof companyProfileSchema>

export interface CompanyProfile {
  legalName: string | null
  legalNameAr: string | null
  vatNumber: string | null
  crNumber: string | null
  address: string | null
  addressAr: string | null
  phone: string | null
  email: string | null
  version: number
  /**
   * The current logo's file id (it changes on every replacement, so it also busts caches):
   * `GET /settings/company/logo` serves it. Set through `PUT` / `DELETE /settings/company/logo`,
   * never through `PUT /settings/company` (ASM-069).
   */
  logoFileId: string | null
  /** Whether a logo is set (`logoFileId !== null`). */
  hasLogo: boolean
  /** The tenant's name as the app shows it (settings Q9). */
  displayName: string
  displayNameAr: string | null
  /**
   * The authorised signature and the company stamp (`GET /settings/company/signature|stamp`), set
   * through `PUT` / `DELETE` there. They print on posted documents only (settings Q3).
   */
  signatureFileId: string | null
  hasSignature: boolean
  stampFileId: string | null
  hasStamp: boolean
}
